import { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import { OrbitControls, Environment } from '@react-three/drei'
import * as THREE from 'three'
import { CubeNotation } from '../types'

// Define cube colors with brighter, more modern shades
const CUBE_COLORS = {
  white: '#FFFFFF',
  yellow: '#FFE100',  // More saturated yellow
  red: '#DC0000',     // Pure red
  orange: '#FF6B00',  // Brighter, more vivid orange
  green: '#00B300',   // More saturated green
  blue: '#0046FF',    // Changed to a more vibrant, consistent blue (from #0051BA)
  black: '#000000',   // Pure black for internals
}

// Position notation to coordinate mapping
const POSITION_NOTATION: Record<string, [number, number, number]> = {
  // Centers
  'U': [0, 1, 0], 'D': [0, -1, 0],
  'R': [1, 0, 0], 'L': [-1, 0, 0],
  'F': [0, 0, 1], 'B': [0, 0, -1],
  // Edges
  'UF': [0, 1, 1], 'UB': [0, 1, -1], 'UR': [1, 1, 0], 'UL': [-1, 1, 0],
  'DF': [0, -1, 1], 'DB': [0, -1, -1], 'DR': [1, -1, 0], 'DL': [-1, -1, 0],
  'FR': [1, 0, 1], 'FL': [-1, 0, 1], 'BR': [1, 0, -1], 'BL': [-1, 0, -1],
  // Corners
  'UFR': [1, 1, 1], 'UFL': [-1, 1, 1], 'UBR': [1, 1, -1], 'UBL': [-1, 1, -1],
  'DFR': [1, -1, 1], 'DFL': [-1, -1, 1], 'DBR': [1, -1, -1], 'DBL': [-1, -1, -1],
}

// Get notation from position coordinates
const getNotationFromPosition = (position: [number, number, number]): string | null => {
  const [x, y, z] = position
  for (const [notation, pos] of Object.entries(POSITION_NOTATION)) {
    if (pos[0] === x && pos[1] === y && pos[2] === z) {
      return notation
    }
  }
  return null
}

// Check if a position is visible based on visiblePieces array
const isPositionVisible = (position: [number, number, number], visiblePieces?: string[]): boolean => {
  if (!visiblePieces || visiblePieces.length === 0) return true
  const notation = getNotationFromPosition(position)
  if (!notation) return true
  return visiblePieces.includes(notation)
}

interface CubeletProps {
  position: [number, number, number]
  colors: string[]
  isVisible?: boolean
  hiddenOpacity?: number
}

const Cubelet = ({ position, colors, isVisible = true, hiddenOpacity = 0.15 }: CubeletProps) => {
  const mesh = useRef<THREE.Mesh>(null)

  return (
    <mesh ref={mesh} position={position}>
      <boxGeometry args={[0.95, 0.95, 0.95]} />
      {colors.map((color, index) => (
        <meshStandardMaterial
          key={index}
          attach={`material-${index}`}
          color={isVisible ? color : '#222222'}
          roughness={0.9}
          metalness={0}
          transparent={!isVisible}
          opacity={isVisible ? 1 : hiddenOpacity}
        />
      ))}

      {/* Inner black faces */}
      <mesh scale={[0.98, 0.98, 0.98]}>
        <boxGeometry args={[0.95, 0.95, 0.95]} />
        <meshStandardMaterial
          color="#000000"
          roughness={1}
          metalness={0}
          transparent={!isVisible}
          opacity={isVisible ? 1 : hiddenOpacity}
        />
      </mesh>
    </mesh>
  )
}

interface CubeSceneProps {
  currentMove?: CubeNotation | string
  onMoveComplete?: () => void
  initialMoves?: string[]  // Moves to apply instantly on mount (no animation)
  visiblePieces?: string[]  // Pieces to show fully (e.g., ['U', 'UF', 'UFR'])
  hiddenOpacity?: number    // Opacity for hidden pieces (default 0.15)
}

// Separate component for the 3D scene
const CubeScene = forwardRef(({ currentMove, onMoveComplete, initialMoves, visiblePieces, hiddenOpacity = 0.15 }: CubeSceneProps, ref) => {
  const cubeRef = useRef<THREE.Group>(null)
  const layerRef = useRef<THREE.Group>(null)
  const controlsRef = useRef<any>(null)
  const [isAnimating, setIsAnimating] = useState(false)
  const [rotationAxis, setRotationAxis] = useState<'x' | 'y' | 'z'>('y')
  const [rotationAngle, setRotationAngle] = useState(0)
  const [targetAngle, setTargetAngle] = useState(0)
  const [selectedCubelets, setSelectedCubelets] = useState<THREE.Object3D[]>([])
  const [isInitialized, setIsInitialized] = useState(false)

  // Expose resetView method
  useImperativeHandle(ref, () => ({
    resetView: () => {
      if (controlsRef.current) {
        controlsRef.current.reset()
      }
    }
  }))

  // Helper function to create colors array for each cubelet
  // Standard orientation: White top, Red front (matching our scanner)
  const createCubeletColors = (position: [number, number, number]): string[] => {
    const colors = Array(6).fill(CUBE_COLORS.black)
    const [x, y, z] = position

    // Right/Left faces (x-axis)
    if (x === 1) colors[0] = CUBE_COLORS.blue     // Right face (Blue)
    if (x === -1) colors[1] = CUBE_COLORS.green   // Left face (Green)

    // Up/Down faces (y-axis)
    if (y === 1) colors[2] = CUBE_COLORS.white    // Up face (White)
    if (y === -1) colors[3] = CUBE_COLORS.yellow  // Down face (Yellow)

    // Front/Back faces (z-axis)
    if (z === 1) colors[4] = CUBE_COLORS.red      // Front face (Red)
    if (z === -1) colors[5] = CUBE_COLORS.orange  // Back face (Orange)

    return colors
  }

  // Initial cube state
  const initialCubelets = [
    // Front face (z = 1)
    { position: [-1, 1, 1] as [number, number, number], colors: createCubeletColors([-1, 1, 1]) },
    { position: [0, 1, 1] as [number, number, number], colors: createCubeletColors([0, 1, 1]) },
    { position: [1, 1, 1] as [number, number, number], colors: createCubeletColors([1, 1, 1]) },
    { position: [-1, 0, 1] as [number, number, number], colors: createCubeletColors([-1, 0, 1]) },
    { position: [0, 0, 1] as [number, number, number], colors: createCubeletColors([0, 0, 1]) },
    { position: [1, 0, 1] as [number, number, number], colors: createCubeletColors([1, 0, 1]) },
    { position: [-1, -1, 1] as [number, number, number], colors: createCubeletColors([-1, -1, 1]) },
    { position: [0, -1, 1] as [number, number, number], colors: createCubeletColors([0, -1, 1]) },
    { position: [1, -1, 1] as [number, number, number], colors: createCubeletColors([1, -1, 1]) },
    // Middle layer (z = 0)
    { position: [-1, 1, 0] as [number, number, number], colors: createCubeletColors([-1, 1, 0]) },
    { position: [0, 1, 0] as [number, number, number], colors: createCubeletColors([0, 1, 0]) },
    { position: [1, 1, 0] as [number, number, number], colors: createCubeletColors([1, 1, 0]) },
    { position: [-1, 0, 0] as [number, number, number], colors: createCubeletColors([-1, 0, 0]) },
    { position: [1, 0, 0] as [number, number, number], colors: createCubeletColors([1, 0, 0]) },
    { position: [-1, -1, 0] as [number, number, number], colors: createCubeletColors([-1, -1, 0]) },
    { position: [0, -1, 0] as [number, number, number], colors: createCubeletColors([0, -1, 0]) },
    { position: [1, -1, 0] as [number, number, number], colors: createCubeletColors([1, -1, 0]) },
    // Back face (z = -1)
    { position: [-1, 1, -1] as [number, number, number], colors: createCubeletColors([-1, 1, -1]) },
    { position: [0, 1, -1] as [number, number, number], colors: createCubeletColors([0, 1, -1]) },
    { position: [1, 1, -1] as [number, number, number], colors: createCubeletColors([1, 1, -1]) },
    { position: [-1, 0, -1] as [number, number, number], colors: createCubeletColors([-1, 0, -1]) },
    { position: [0, 0, -1] as [number, number, number], colors: createCubeletColors([0, 0, -1]) },
    { position: [1, 0, -1] as [number, number, number], colors: createCubeletColors([1, 0, -1]) },
    { position: [-1, -1, -1] as [number, number, number], colors: createCubeletColors([-1, -1, -1]) },
    { position: [0, -1, -1] as [number, number, number], colors: createCubeletColors([0, -1, -1]) },
    { position: [1, -1, -1] as [number, number, number], colors: createCubeletColors([1, -1, -1]) },
  ]

  // Helper function to select cubelets for a move
  const selectCubeletsForMove = (move: CubeNotation | string) => {
    if (!cubeRef.current) return []

    const allCubelets = cubeRef.current.children as THREE.Object3D[]

    // Normalize move - remove 2 suffix and handle prime notation
    const baseMove = move.replace(/2$/, '').replace(/'$/, '')

    switch (baseMove) {
      // Face moves
      case 'U':
        return allCubelets.filter(cubelet => cubelet.position.y > 0.5)
      case 'D':
        return allCubelets.filter(cubelet => cubelet.position.y < -0.5)
      case 'R':
        return allCubelets.filter(cubelet => cubelet.position.x > 0.5)
      case 'L':
        return allCubelets.filter(cubelet => cubelet.position.x < -0.5)
      case 'F':
        return allCubelets.filter(cubelet => cubelet.position.z > 0.5)
      case 'B':
        return allCubelets.filter(cubelet => cubelet.position.z < -0.5)

      // Middle layer moves
      case 'M':
        return allCubelets.filter(cubelet => Math.abs(cubelet.position.x) < 0.5)
      case 'E':
        return allCubelets.filter(cubelet => Math.abs(cubelet.position.y) < 0.5)
      case 'S':
        return allCubelets.filter(cubelet => Math.abs(cubelet.position.z) < 0.5)

      // Wide moves (layer + middle)
      case 'r':
      case 'Rw':
        return allCubelets.filter(cubelet => cubelet.position.x > -0.5) // R + M
      case 'l':
      case 'Lw':
        return allCubelets.filter(cubelet => cubelet.position.x < 0.5)  // L + M'
      case 'u':
      case 'Uw':
        return allCubelets.filter(cubelet => cubelet.position.y > -0.5) // U + E'
      case 'd':
      case 'Dw':
        return allCubelets.filter(cubelet => cubelet.position.y < 0.5)  // D + E
      case 'f':
      case 'Fw':
        return allCubelets.filter(cubelet => cubelet.position.z > -0.5) // F + S
      case 'b':
      case 'Bw':
        return allCubelets.filter(cubelet => cubelet.position.z < 0.5)  // B + S'

      // Cube rotations (all cubelets)
      case 'x':
        return [...allCubelets]
      case 'y':
        return [...allCubelets]
      case 'z':
        return [...allCubelets]

      default:
        return []
    }
  }

  // Apply initial moves instantly (no animation) on mount
  useEffect(() => {
    if (!initialMoves || initialMoves.length === 0 || isInitialized || !cubeRef.current) {
      setIsInitialized(true)
      return
    }

    // Apply each move instantly
    const applyMoveInstantly = (move: string) => {
      if (!cubeRef.current) return

      const moveConfig = getMoveConfig(move)
      const allCubelets = cubeRef.current.children as THREE.Object3D[]

      // Normalize move for selection
      const baseMove = move.replace(/2$/, '').replace(/'$/, '')

      // Select cubelets based on move
      let cubeletsToMove: THREE.Object3D[] = []
      switch (baseMove) {
        case 'U':
          cubeletsToMove = allCubelets.filter(c => c.position.y > 0.5)
          break
        case 'D':
          cubeletsToMove = allCubelets.filter(c => c.position.y < -0.5)
          break
        case 'R':
          cubeletsToMove = allCubelets.filter(c => c.position.x > 0.5)
          break
        case 'L':
          cubeletsToMove = allCubelets.filter(c => c.position.x < -0.5)
          break
        case 'F':
          cubeletsToMove = allCubelets.filter(c => c.position.z > 0.5)
          break
        case 'B':
          cubeletsToMove = allCubelets.filter(c => c.position.z < -0.5)
          break
        case 'M':
          cubeletsToMove = allCubelets.filter(c => Math.abs(c.position.x) < 0.5)
          break
        case 'E':
          cubeletsToMove = allCubelets.filter(c => Math.abs(c.position.y) < 0.5)
          break
        case 'S':
          cubeletsToMove = allCubelets.filter(c => Math.abs(c.position.z) < 0.5)
          break
        case 'r':
        case 'Rw':
          cubeletsToMove = allCubelets.filter(c => c.position.x > -0.5)
          break
        case 'l':
        case 'Lw':
          cubeletsToMove = allCubelets.filter(c => c.position.x < 0.5)
          break
        case 'u':
        case 'Uw':
          cubeletsToMove = allCubelets.filter(c => c.position.y > -0.5)
          break
        case 'd':
        case 'Dw':
          cubeletsToMove = allCubelets.filter(c => c.position.y < 0.5)
          break
        case 'f':
        case 'Fw':
          cubeletsToMove = allCubelets.filter(c => c.position.z > -0.5)
          break
        case 'b':
        case 'Bw':
          cubeletsToMove = allCubelets.filter(c => c.position.z < 0.5)
          break
        case 'x':
        case 'y':
        case 'z':
          cubeletsToMove = [...allCubelets]
          break
      }

      // Apply rotation instantly to each cubelet
      const axis = new THREE.Vector3(
        moveConfig.axis === 'x' ? 1 : 0,
        moveConfig.axis === 'y' ? 1 : 0,
        moveConfig.axis === 'z' ? 1 : 0
      )

      cubeletsToMove.forEach(cubelet => {
        // Rotate position around axis
        cubelet.position.applyAxisAngle(axis, moveConfig.angle)
        cubelet.position.round()

        // Rotate the cubelet itself
        cubelet.rotateOnWorldAxis(axis, moveConfig.angle)
      })
    }

    // Apply all initial moves
    initialMoves.forEach(move => {
      applyMoveInstantly(move)
    })

    setIsInitialized(true)
  }, [initialMoves, isInitialized])

  // Handle move animations
  useEffect(() => {
    if (currentMove && !isAnimating && cubeRef.current && layerRef.current) {
      const moveConfig = getMoveConfig(currentMove)
      const cubeletsToMove = selectCubeletsForMove(currentMove)
      
      if (cubeletsToMove.length > 0) {
        setRotationAxis(moveConfig.axis)
        setTargetAngle(moveConfig.angle)
        setIsAnimating(true)
        setSelectedCubelets(cubeletsToMove)

        // Move selected cubelets to the rotation layer
        layerRef.current.clear()
        cubeletsToMove.forEach(cubelet => {
          layerRef.current?.add(cubelet)
        })
      }
    }
  }, [currentMove])

  // Animation frame
  useFrame(() => {
    if (isAnimating && layerRef.current) {
      const step = 0.025  // Reduced from 0.05 for even slower movement
      const newAngle = rotationAngle + step

      if (newAngle >= Math.abs(targetAngle)) {
        // Ensure we complete the rotation exactly
        layerRef.current.rotation[rotationAxis] = targetAngle

        // Move cubelets back to main cube group
        if (cubeRef.current) {
          // First update the world matrix of the rotation layer
          layerRef.current.updateMatrixWorld(true)
          
          selectedCubelets.forEach(cubelet => {
            // Get the world position and rotation before removing from rotation layer
            const worldPos = new THREE.Vector3()
            const worldQuat = new THREE.Quaternion()
            const worldScale = new THREE.Vector3()
            
            cubelet.matrixWorld.decompose(worldPos, worldQuat, worldScale)
            
            // Remove from rotation layer first
            layerRef.current?.remove(cubelet)
            
            // Reset the cubelet's local transform
            cubelet.position.copy(worldPos)
            cubelet.quaternion.copy(worldQuat)
            cubelet.scale.copy(worldScale)

            // Round the position to fix any floating point errors
            cubelet.position.round()
            
            // Add to the main cube group
            cubeRef.current?.add(cubelet)
          })
        }

        // Reset the rotation layer
        layerRef.current.rotation[rotationAxis] = 0
        setRotationAngle(0)
        setIsAnimating(false)
        setTargetAngle(0)
        setSelectedCubelets([])
        
        if (onMoveComplete) onMoveComplete()
      } else {
        // Apply easing to simulate magnetic behavior
        const progress = rotationAngle / Math.abs(targetAngle)
        
        const easeInOutBack = (x: number): number => {
          const c1 = 1.70158
          const c2 = c1 * 1.525

          return x < 0.5
            ? (Math.pow(2 * x, 2) * ((c2 + 1) * 2 * x - c2)) / 2
            : (Math.pow(2 * x - 2, 2) * ((c2 + 1) * (x * 2 - 2) + c2) + 2) / 2
        }

        const easedAngle = easeInOutBack(progress) * targetAngle
        setRotationAngle(newAngle)
        layerRef.current.rotation[rotationAxis] = easedAngle
      }
    }
  })

  return (
    <>
      <ambientLight intensity={0.7} />
      <directionalLight position={[5, 5, 5]} intensity={0.4} />
      <directionalLight position={[-5, -5, -5]} intensity={0.2} />
      <Environment preset="studio" />
      <group>
        <group ref={cubeRef}>
          {initialCubelets.map((cubelet, index) => (
            <Cubelet
              key={index}
              position={cubelet.position}
              colors={cubelet.colors}
              isVisible={isPositionVisible(cubelet.position, visiblePieces)}
              hiddenOpacity={hiddenOpacity}
            />
          ))}
        </group>
        <group ref={layerRef} />
      </group>
      <OrbitControls 
        ref={controlsRef}
        enableZoom={true} 
        enablePan={false}
        minDistance={7}
        maxDistance={12}
        target={[0, 0, 0]}
        minPolarAngle={Math.PI / 6}
        maxPolarAngle={Math.PI * 5/6}
        enableDamping={true}
        dampingFactor={0.05}
      />
    </>
  )
})

interface RubiksCubeProps {
  currentMove?: CubeNotation | string
  onMoveComplete?: () => void
  initialMoves?: string[]  // Moves to apply instantly on mount (no animation)
  visiblePieces?: string[]  // Pieces to show fully (e.g., ['U', 'UF', 'UFR'])
  hiddenOpacity?: number    // Opacity for hidden pieces (default 0.15)
}

const RubiksCube = forwardRef<any, RubiksCubeProps>((props, ref) => {
  const controlsRef = useRef<any>(null)
  
  return (
    <Canvas camera={{ position: [2, 4, 8], fov: 30 }}>
      <CubeScene ref={ref} {...props} />
      <OrbitControls 
        ref={controlsRef}
        enableZoom={false} 
        enablePan={false}
        enableRotate={false}
        target={[0, 0, 0]}
      />
    </Canvas>
  )
})

// Helper function to get rotation configuration for a move
const getMoveConfig = (move: CubeNotation | string): { axis: 'x' | 'y' | 'z'; angle: number } => {
  const QUARTER = Math.PI / 2
  const HALF = Math.PI

  // Check for double move (ends with 2)
  const isDouble = move.endsWith('2')
  const isPrime = move.includes("'")
  const baseMove = move.replace(/2$/, '').replace(/'$/, '')

  // Base configurations (clockwise direction)
  const baseConfig: Record<string, { axis: 'x' | 'y' | 'z'; dir: number }> = {
    // Face moves
    R: { axis: 'x', dir: -1 },
    L: { axis: 'x', dir: 1 },
    U: { axis: 'y', dir: -1 },
    D: { axis: 'y', dir: 1 },
    F: { axis: 'z', dir: -1 },
    B: { axis: 'z', dir: 1 },

    // Middle layer moves
    M: { axis: 'x', dir: 1 },  // Same direction as L
    E: { axis: 'y', dir: 1 },  // Same direction as D
    S: { axis: 'z', dir: -1 }, // Same direction as F

    // Wide moves (same direction as corresponding face)
    r: { axis: 'x', dir: -1 },
    l: { axis: 'x', dir: 1 },
    u: { axis: 'y', dir: -1 },
    d: { axis: 'y', dir: 1 },
    f: { axis: 'z', dir: -1 },
    b: { axis: 'z', dir: 1 },
    Rw: { axis: 'x', dir: -1 },
    Lw: { axis: 'x', dir: 1 },
    Uw: { axis: 'y', dir: -1 },
    Dw: { axis: 'y', dir: 1 },
    Fw: { axis: 'z', dir: -1 },
    Bw: { axis: 'z', dir: 1 },

    // Cube rotations
    x: { axis: 'x', dir: -1 }, // Same direction as R
    y: { axis: 'y', dir: -1 }, // Same direction as U
    z: { axis: 'z', dir: -1 }, // Same direction as F
  }

  const config = baseConfig[baseMove]
  if (!config) {
    return { axis: 'y', angle: 0 }
  }

  // Calculate angle based on move modifiers
  let angle = isDouble ? HALF : QUARTER
  if (isPrime) {
    angle = -angle
  }
  angle = angle * config.dir

  return { axis: config.axis, angle }
}

export default RubiksCube 